(function() {
  'use strict';

  if (!window.customElements || window.customElements.get('comenii-tiered-gifts')) {
    return;
  }

  class ComeniiTieredGifts extends HTMLElement {
    connectedCallback() {
      this.tier2Quantity = Math.max(2, Number(this.readAttribute('data-tier2-quantity', 'data-tier-2-quantity') || 2));
      this.tier3Quantity = Math.max(
        this.tier2Quantity + 1,
        Number(this.readAttribute('data-tier3-quantity', 'data-tier-3-quantity') || 3)
      );
      this.tier1VariantId = Number(this.readAttribute('data-tier1-variant-id', 'data-tier-1-variant-id') || 0);
      this.tier1ProductId = String(this.readAttribute('data-tier1-product-id', 'data-tier-1-product-id') || '');
      this.tier1Available = this.readAttribute('data-tier1-available', 'data-tier-1-available') === 'true';
      this.tier2VariantId = Number(this.readAttribute('data-tier2-variant-id', 'data-tier-2-variant-id') || 0);
      this.tier2ProductId = String(this.readAttribute('data-tier2-product-id', 'data-tier-2-product-id') || '');
      this.tier2Available = this.readAttribute('data-tier2-available', 'data-tier-2-available') === 'true';
      this.tier3VariantId = Number(this.readAttribute('data-tier3-variant-id', 'data-tier-3-variant-id') || 0);
      this.tier3ProductId = String(this.readAttribute('data-tier3-product-id', 'data-tier-3-product-id') || '');
      this.tier3Available = this.readAttribute('data-tier3-available', 'data-tier-3-available') === 'true';
      this.configuredGiftProductIds = [
        this.tier1ProductId,
        this.tier2ProductId,
        this.tier3ProductId
      ].filter(Boolean);

      this.message = this.querySelector('[data-gift-message]');
      this.progressBar = this.querySelector('[data-gift-progress-bar]');
      this.milestones = Array.from(this.querySelectorAll('[data-gift-milestone]'));
      this.tierCards = Array.from(this.querySelectorAll('[data-gift-tier-card]'));
      this.tierStatuses = Array.from(this.querySelectorAll('[data-gift-tier-status]'));
      this.toast = this.querySelector('[data-gift-toast]');
      this.error = this.querySelector('[data-gift-error]');
      this.currentCart = null;
      this.currentState = null;
      this.busy = false;
      this.reconcileTimer = null;

      this.onExternalCartUpdate = this.handleExternalCartUpdate.bind(this);
      this.onDelayedRefresh = this.delayedRefresh.bind(this);

      document.addEventListener('cart:updated', this.onExternalCartUpdate);
      document.addEventListener('ajaxProduct:added', this.onDelayedRefresh);
      document.addEventListener('cart:build', this.onDelayedRefresh);
      document.addEventListener('drawerOpen.CartDrawer', this.onDelayedRefresh);

      if (!window.__comeniiGiftOwner || !document.documentElement.contains(window.__comeniiGiftOwner)) {
        window.__comeniiGiftOwner = this;
      }

      this.refresh();
    }

    readAttribute(primaryName, legacyName) {
      return this.getAttribute(primaryName) || this.getAttribute(legacyName) || '';
    }

    disconnectedCallback() {
      window.clearTimeout(this.reconcileTimer);
      document.removeEventListener('cart:updated', this.onExternalCartUpdate);
      document.removeEventListener('ajaxProduct:added', this.onDelayedRefresh);
      document.removeEventListener('cart:build', this.onDelayedRefresh);
      document.removeEventListener('drawerOpen.CartDrawer', this.onDelayedRefresh);

      if (window.__comeniiGiftOwner === this) {
        window.__comeniiGiftOwner = null;
      }
    }

    delayedRefresh() {
      window.setTimeout(() => this.refresh(), 140);
    }

    handleExternalCartUpdate(event) {
      if (event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)) {
        this.applyCart(event.detail.cart);
        return;
      }
      this.delayedRefresh();
    }

    getCartUrl() {
      return window.theme && theme.routes && theme.routes.cart ? theme.routes.cart : '/cart.js';
    }

    getAddUrl() {
      return window.theme && theme.routes && theme.routes.cartAdd ? theme.routes.cartAdd : '/cart/add.js';
    }

    getUpdateUrl() {
      return window.theme && theme.routes && theme.routes.cartUpdate ? theme.routes.cartUpdate : '/cart/update.js';
    }

    async refresh() {
      if (this.busy) return;

      try {
        const response = await fetch(this.getCartUrl(), {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Cart request failed');
        this.applyCart(await response.json());
      } catch (error) {
        this.showError(error);
      }
    }

    applyCart(cart, options) {
      if (!cart || !Array.isArray(cart.items)) return;
      const shouldReconcile = !options || options.reconcile !== false;

      const giftItems = cart.items.filter((item) => this.isGiftItem(item));
      const qualifyingItems = cart.items.filter((item) => {
        return !this.isGiftItem(item) && !this.isConfiguredGiftProduct(item);
      });
      const paidQuantity = qualifyingItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
      const tier1Unlocked = paidQuantity > 0;
      const tier2Unlocked = paidQuantity >= this.tier2Quantity;
      const tier3Unlocked = paidQuantity >= this.tier3Quantity;

      const state = {
        giftItems,
        paidItems: qualifyingItems,
        paidQuantity,
        tier1Unlocked,
        tier2Unlocked,
        tier3Unlocked
      };

      this.currentCart = cart;
      this.currentState = state;
      this.hidden = paidQuantity === 0 && giftItems.length === 0;
      this.renderState(state);
      this.hideError();

      if (shouldReconcile && window.__comeniiGiftOwner === this && !window.__comeniiGiftMutation) {
        this.scheduleReconcile();
      }
    }

    scheduleReconcile() {
      window.clearTimeout(this.reconcileTimer);
      this.reconcileTimer = window.setTimeout(() => this.refreshAndReconcile(), 320);
    }

    async refreshAndReconcile() {
      if (this.busy || window.__comeniiGiftMutation || window.__comeniiGiftOwner !== this) return;

      try {
        const response = await fetch(this.getCartUrl(), {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Cart request failed');
        this.applyCart(await response.json(), { reconcile: false });
        await this.reconcileCart(this.currentState);
      } catch (error) {
        this.showError(error);
      }
    }

    renderState(state) {
      const progressRange = this.tier3Quantity - 1;
      const percentage = progressRange > 0
        ? Math.min(100, Math.max(0, Math.round(((state.paidQuantity - 1) / progressRange) * 100)))
        : 0;

      if (this.progressBar) this.progressBar.style.width = percentage + '%';

      if (!state.tier1Unlocked) {
        this.message.textContent = '';
      } else if (state.tier3Unlocked) {
        this.message.textContent = this.dataset.allUnlockedText;
      } else if (state.tier2Unlocked) {
        this.renderQuantityMessage(this.tier3Quantity - state.paidQuantity, true);
      } else {
        this.renderQuantityMessage(this.tier2Quantity - state.paidQuantity, false);
      }

      this.setTierState(1, state.tier1Unlocked);
      this.setTierState(2, state.tier2Unlocked);
      this.setTierState(3, state.tier3Unlocked);
    }

    setTierState(tier, unlocked) {
      const card = this.tierCards.find((element) => element.dataset.giftTierCard === String(tier));
      const milestone = this.milestones.find((element) => element.dataset.giftMilestone === String(tier));
      const status = this.tierStatuses.find((element) => element.dataset.giftTierStatus === String(tier));

      if (card) {
        card.classList.toggle('is-unlocked', unlocked);
        card.classList.toggle('is-locked', !unlocked);
      }
      if (milestone) milestone.classList.toggle('is-active', unlocked);
      if (status) status.textContent = unlocked ? '✓' : String(tier);
    }

    renderQuantityMessage(remainingQuantity, finalTier) {
      if (!this.message) return;
      const quantity = Math.max(1, Number(remainingQuantity || 1));
      const key = finalTier
        ? (quantity === 1 ? 'nextGiftOneText' : 'nextGiftManyText')
        : (quantity === 1 ? 'addMoreOneText' : 'addMoreManyText');
      const text = this.dataset[key] || '';
      const parts = text.split('__count__');
      const count = document.createElement('strong');
      count.textContent = String(quantity);

      this.message.replaceChildren(
        document.createTextNode(parts[0] || ''),
        count,
        document.createTextNode(parts.slice(1).join('__count__'))
      );
    }

    isGiftItem(item) {
      return Boolean(item.properties && String(item.properties._comenii_free_gift) === 'true');
    }

    isConfiguredGiftProduct(item) {
      return this.configuredGiftProductIds.includes(String(item.product_id || ''));
    }

    giftTier(item) {
      if (!item.properties) return '';
      return String(item.properties._comenii_gift_tier || '');
    }

    async reconcileCart(state) {
      if (this.busy || window.__comeniiGiftMutation) return;

      const updates = {};
      const additions = [];
      const tierGroups = { '1': [], '2': [], '3': [] };

      state.giftItems.forEach((item) => {
        const tier = this.giftTier(item);
        if (!tierGroups[tier]) {
          updates[item.key] = 0;
          return;
        }
        tierGroups[tier].push(item);
      });

      const tier1Kept = this.reconcileFixedTier(
        tierGroups['1'],
        state.tier1Unlocked,
        this.tier1VariantId,
        updates
      );

      if (state.tier1Unlocked && !tier1Kept && this.tier1Available && this.tier1VariantId) {
        additions.push(this.giftPayload(this.tier1VariantId, 1));
      }

      const tier2Kept = this.reconcileFixedTier(
        tierGroups['2'],
        state.tier2Unlocked,
        this.tier2VariantId,
        updates
      );

      if (state.tier2Unlocked && !tier2Kept && this.tier2Available && this.tier2VariantId) {
        additions.push(this.giftPayload(this.tier2VariantId, 2));
      }

      const tier3Kept = this.reconcileFixedTier(
        tierGroups['3'],
        state.tier3Unlocked,
        this.tier3VariantId,
        updates
      );

      if (state.tier3Unlocked && !tier3Kept && this.tier3Available && this.tier3VariantId) {
        additions.push(this.giftPayload(this.tier3VariantId, 3));
      }

      if (Object.keys(updates).length) {
        const removedForEligibility = state.giftItems.some((item) => {
          const tier = this.giftTier(item);
          return (tier === '1' && !state.tier1Unlocked) ||
            (tier === '2' && !state.tier2Unlocked) ||
            (tier === '3' && !state.tier3Unlocked);
        });
        await this.updateCart(updates, removedForEligibility);
        return;
      }

      if (additions.length) {
        await this.addGiftItems(additions);
      }
    }

    reconcileFixedTier(items, eligible, expectedVariantId, updates) {
      let kept = false;

      items.forEach((item) => {
        const matchesVariant = Number(item.variant_id) === Number(expectedVariantId);
        if (!eligible || !matchesVariant || kept) {
          updates[item.key] = 0;
          return;
        }
        kept = true;
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
      });

      return kept;
    }

    giftPayload(variantId, tier) {
      return {
        id: Number(variantId),
        quantity: 1,
        properties: {
          _comenii_free_gift: 'true',
          _comenii_gift_tier: String(tier)
        }
      };
    }

    async updateCart(updates, showRemovedMessage) {
      this.setBusy(true);

      try {
        await this.requestCartUpdate(updates);
        if (showRemovedMessage) this.showToast(this.dataset.removedText);
        await this.afterMutation();
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    }

    async addGiftItems(items) {
      this.setBusy(true);

      try {
        await this.requestGiftAdd(items);
        await this.afterMutation();
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    }

    async requestCartUpdate(updates) {
      const response = await fetch(this.getUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Gift update failed');
      return response.json();
    }

    async requestGiftAdd(items, attempt) {
      const retryNumber = Number(attempt || 0);
      const response = await fetch(this.getAddUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });
      if (!response.ok) {
        const detail = await this.readErrorResponse(response);
        const retryableStatus = [409, 422, 429, 500, 502, 503, 504].includes(response.status);

        if (retryableStatus && retryNumber < 2) {
          await this.wait(300 * (retryNumber + 1));
          return this.requestGiftAdd(items, retryNumber + 1);
        }

        const error = new Error(detail || 'Gift add failed');
        error.status = response.status;
        throw error;
      }

      const contentType = response.headers.get('content-type') || '';
      return contentType.includes('application/json') ? response.json() : null;
    }

    async readErrorResponse(response) {
      try {
        const data = await response.clone().json();
        return data.description || data.message || data.status || '';
      } catch (error) {
        try {
          return await response.text();
        } catch (textError) {
          return '';
        }
      }
    }

    wait(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    async afterMutation() {
      const response = await fetch(this.getCartUrl(), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Cart refresh failed');
      const updatedCart = await response.json();

      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      this.applyCart(updatedCart);
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      window.__comeniiGiftMutation = isBusy;
      this.setAttribute('aria-busy', String(isBusy));
    }

    showToast(message) {
      if (!this.toast || !message) return;
      this.toast.textContent = message;
      this.toast.hidden = false;
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => { this.toast.hidden = true; }, 4200);
    }

    showError(error) {
      if (window.console && typeof window.console.error === 'function' && error) {
        window.console.error('[COMENII tiered gifts]', error);
      }
      if (!this.error) return;
      this.error.textContent = this.decodeEntities(this.dataset.errorText || '');
      this.error.hidden = false;
    }

    decodeEntities(text) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  window.customElements.define('comenii-tiered-gifts', ComeniiTieredGifts);
})();
